import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { CompanyService, NameFilter, LocalStorageService } from '@ops/shared-types';
import { PaymentMethodsManagerService } from './payment-methods-manager.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyChangeListenerService {
  private companyChangeSubject = new Subject<NameFilter>();
  private subscription!: Subscription;
  private lastCompanyId: string | null = null;

  constructor(
    private companyService: CompanyService,
    private localStorageService: LocalStorageService,
    private paymentMethodsManager: PaymentMethodsManagerService
  ) {
    this.initializeListener();
  }

  private initializeListener(): void {
    // Subscribe to company changes
    this.subscription = this.companyService.selectedCompany$
      .pipe(
        filter(company => !!company?._id),
        distinctUntilChanged((prev, curr) => prev?._id === curr?._id)
      )
      .subscribe(company => {
        this.handleCompanyChange(company);
      });
  }

  private async handleCompanyChange(company: NameFilter): Promise<void> {
    const companyId = company._id;
    
    // Skip if it's the same company
    if (this.lastCompanyId === companyId) {
      return;
    }

    // console.log(`Company changed from ${this.lastCompanyId} to ${companyId}`);
    this.lastCompanyId = companyId;

    try {
      // Clear previous payment methods state
      this.paymentMethodsManager.clearState();
      
      // Load payment methods for the new company
      await this.paymentMethodsManager.loadAllPaymentMethods(companyId, true).toPromise();
      
      // Emit company change event
      this.companyChangeSubject.next(company);
      
      // console.log(`Payment methods loaded for company: ${companyId}`);
    } catch (error) {
      console.error('Error loading payment methods for new company:', error);
    }
  }

  // Observable for components to subscribe to company changes
  getCompanyChange$(): Observable<NameFilter> {
    return this.companyChangeSubject.asObservable();
  }

  // Method to manually trigger company change handling
  async triggerCompanyChange(company: NameFilter): Promise<void> {
    await this.handleCompanyChange(company);
  }

  // Get current company ID
  getCurrentCompanyId(): string | null {
    return this.lastCompanyId;
  }

  // Cleanup
  destroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.companyChangeSubject.complete();
  }
}
